import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api, fmtRON } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [booking, setBooking] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          if (data.booking_id) {
            const b = await api.get(`/bookings/${data.booking_id}`).then((r) => r.data).catch(() => null);
            setBooking(b);
          }
          return;
        }
        if (attempts >= 8) { setStatus("timeout"); return; }
        setTimeout(poll, 2000);
      } catch {
        if (attempts >= 8) { setStatus("error"); return; }
        setTimeout(poll, 2000);
      }
    };
    poll();
  }, [sessionId]);

  return (
    <div className="App">
      <Navbar />
      <div className="max-w-lg mx-auto px-4 py-24 text-center">
        {status === "checking" && (
          <div className="animate-fade-up">
            <Loader2 className="h-14 w-14 text-accent animate-spin mx-auto" />
            <h1 className="font-heading text-3xl font-semibold mt-6">Confirmăm plata...</h1>
            <p className="text-muted-foreground mt-2">Te rugăm să aștepți câteva secunde.</p>
          </div>
        )}
        {status === "paid" && (
          <div className="animate-fade-up" data-testid="payment-success-message">
            <CheckCircle2 className="h-16 w-16 text-primary mx-auto" />
            <h1 className="font-heading text-4xl font-semibold mt-6">Rezervare confirmată!</h1>
            <p className="text-muted-foreground mt-3">Îți mulțumim! Gazda a fost notificată despre sejurul tău.</p>
            {booking && (
              <div className="mt-8 rounded-2xl border border-border p-6 text-left bg-card">
                <p className="font-medium text-lg">{booking.property_title}</p>
                <div className="mt-3 space-y-1 text-sm font-mono text-muted-foreground">
                  <div className="flex justify-between"><span>Check-in</span><span>{booking.check_in}</span></div>
                  <div className="flex justify-between"><span>Check-out</span><span>{booking.check_out}</span></div>
                  <div className="flex justify-between"><span>Oaspeți</span><span>{booking.guests}</span></div>
                  <div className="flex justify-between font-semibold text-foreground pt-2 border-t border-border">
                    <span>Total plătit</span><span>{fmtRON(booking.total_amount)}</span>
                  </div>
                </div>
              </div>
            )}
            <div className="flex gap-3 justify-center mt-8">
              <Button onClick={() => navigate("/calatorii")} className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground">
                Vezi călătoriile mele
              </Button>
              <Button onClick={() => navigate("/")} variant="outline" className="rounded-full">Acasă</Button>
            </div>
          </div>
        )}
        {(status === "timeout" || status === "error") && (
          <div className="animate-fade-up">
            <XCircle className="h-14 w-14 text-destructive mx-auto" />
            <h1 className="font-heading text-3xl font-semibold mt-6">Nu am putut confirma plata</h1>
            <p className="text-muted-foreground mt-2">Verifică secțiunea „Călătoriile mele" în câteva minute.</p>
            <Button onClick={() => navigate("/calatorii")} className="rounded-full mt-6 bg-accent hover:bg-accent/90 text-accent-foreground">
              Călătoriile mele
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
